/**
 * JUB-154: smart defaults and date helpers for the wizard. Pure functions;
 * `today` is injectable for tests.
 */
import { calendarDaysUntil } from "@/lib/countdown";
import type { MomentSlot, OnboardingType } from "./types";

/**
 * §7 product defaults (to be validated with real usage): a wedding starts with
 * the religious ceremony and the party (civil off); a baptism with the
 * ceremony and the party. For wedding + baptism the church part comes from
 * the "together?" answer, so only the party is preselected here.
 */
export function defaultMoments(type: OnboardingType): MomentSlot[] {
  return type === "wedding_baptism" ? ["party"] : ["religious", "party"];
}

/** Which moments the structure screen offers as checkboxes. */
export function structureOptions(type: OnboardingType): MomentSlot[] {
  if (type === "wedding") return ["civil", "religious", "party"];
  if (type === "baptism") return ["religious", "party"];
  return ["civil", "party"];
}

/** "YYYY-MM-DD" → local midnight; null when malformed. */
export function parseDay(value: string | null | undefined): Date | null {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.getMonth() === Number(match[2]) - 1 ? date : null;
}

export function toDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function startOfToday(today: Date): Date {
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

/**
 * Whole calendar days from today to the date (negative in the past); computed,
 * never stored (§3). The same count the dashboard event card shows
 * (`calendarDaysUntil`, JUB-176); only a strict "YYYY-MM-DD" is accepted.
 */
export function daysUntil(date: string | null | undefined, today: Date = new Date()): number | null {
  return parseDay(date) ? calendarDaysUntil(date, today) : null;
}

/** W03: a past date is accepted but warned about. */
export function isPastDate(date: string | null | undefined, today: Date = new Date()): boolean {
  const days = daysUntil(date, today);
  return days !== null && days < 0;
}

/**
 * W07 default deadline: event date − 14 days. No suggestion when that day is
 * today or already past (the API requires a deadline after today).
 */
export function rsvpDefault(eventDate: string | null | undefined, today: Date = new Date()): string | null {
  const day = parseDay(eventDate);
  if (!day) return null;
  const deadline = new Date(day.getFullYear(), day.getMonth(), day.getDate() - 14);
  return deadline.getTime() > startOfToday(today).getTime() ? toDay(deadline) : null;
}

/** Same rules as the API: after today, on or before the event date. */
export function rsvpDeadlineError(
  deadline: string | null | undefined,
  eventDate: string | null | undefined,
  today: Date = new Date(),
): "invalid" | "after_today" | "after_event_date" | null {
  const day = parseDay(deadline);
  if (!day) return "invalid";
  if (day.getTime() <= startOfToday(today).getTime()) return "after_today";
  const event = parseDay(eventDate);
  if (event && day.getTime() > event.getTime()) return "after_event_date";
  return null;
}

/** "Sâmbătă, 19 septembrie 2026" (W03 reward line). */
export function formatLongDay(date: string | null | undefined, locale: string): string {
  const day = parseDay(date);
  if (!day) return "";
  const text = new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ro-RO", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(day);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "HH:MM" 24h. */
export function isValidTime(value: string | null | undefined): boolean {
  return Boolean(value && /^([01]\d|2[0-3]):[0-5]\d$/.test(value));
}

/** A first name needs at least one letter (W02 validation). */
export function hasLetter(value: string | null | undefined): boolean {
  return Boolean(value && /\p{L}/u.test(value));
}
